import {
    type AggResponse,
	type EntityATI,
	type ExperimentLayer,
	fetchJiraFieldsQuery,
	generateSearchPageCacheKey,
	type JiraHydrationData,
	type ResultsSort,
	type SearchPageData,
	searchPageQuery,
	searchPageQueryCache,
} from '@atlassian/dynamic-cards-search-client';

import { type PrimaryProductKey } from '../../../common/constants/products';
import { type SelectedFiltersType } from '../../../common/constants/types';
import { getEmptySelectedFilters } from '../../../common/utils/filters';

export class ResultsFetchError extends Error {
	constructor(message: string) {
		super(message);
		this.name = 'ResultsFetchError';
	}
}

export class EmptyEntitiesError extends ResultsFetchError {
	constructor() {
		super('No entities provided to fetch results');
		this.name = 'EmptyEntitiesError';
	}
}

export type FetchResultsProps = {
	after?: string;
	aggAbsoluteUrl?: string | undefined;
	cloudId: string;
	entities: EntityATI[];
	experimentId?: string;
	experimentLayers?: ExperimentLayer[];
	limit: number;
	originalQuery?: string;
	query: string;
	searchSessionId: string;
	selectedFilters: SelectedFiltersType | null;
	shadowExperimentId?: string;
	sort: ResultsSort;
	sourceProduct?: PrimaryProductKey;
};

export const fetchResults = async ({
	query,
	originalQuery,
	entities,
	selectedFilters,
	cloudId,
	aggAbsoluteUrl,
	searchSessionId,
	sort,
	sourceProduct,
	experimentId,
	shadowExperimentId,
	experimentLayers,
	after,
	limit,
}: FetchResultsProps): Promise<AggResponse<SearchPageData>> => {
	const { commonFilters, confluenceFilters, jiraFilters, thirdPartyFilters } =
		selectedFilters || getEmptySelectedFilters();

	const sortVariables = [sort];

	// If the original query param is present, we want to disable query replacement
	const disableQueryReplacement = !!originalQuery;

	const variables = {
		query,
		queryVersion: 1,
		cloudIdARI: `ari:cloud:confluence::site/${cloudId}`,
		tenantId: cloudId,
		limit,
		commonFilters,
		confluenceFilters,
		jiraFilters,
		thirdPartyFilters,
		entities,
		searchSessionId,
		sort: sortVariables,
		sourceProduct,
		experimentId,
		shadowExperimentId,
		experimentLayers: experimentLayers?.map(({ name, layerId, shadowId }) => ({
			name,
			layerId,
			shadowId,
		})),
		// eslint-disable-next-line no-constant-condition
		after: false ? after : undefined,
		disableQueryReplacement,
	};

	if (entities.length === 0) {
		throw new EmptyEntitiesError();
	}

	const response = searchPageQuery({
		variables,
		aggAbsoluteUrl,
		originalQuery,
	});

	// If the original query param is not present, we want to cache the response with the replacement query
	// fg('rovo-search-spelling-correction') => false
	// eslint-disable-next-line no-constant-condition
	if (!originalQuery && false) {
		const value = await response;
		// eslint-disable-next-line @typescript-eslint/no-non-null-asserted-optional-chain
		const queryInfo = value.data?.search.results.queryInfo!;
		if (
			queryInfo &&
			queryInfo.replacementQuery &&
			queryInfo.originalQuery &&
			queryInfo.replacementQuery !== queryInfo.originalQuery
		) {
			searchPageQueryCache.set(
				generateSearchPageCacheKey(
					{ ...variables, query: queryInfo.replacementQuery! },
					queryInfo.originalQuery,
				),
				response,
			);
		}
	}

	return response;
};

export const fetchJiraFields = async ({
	issueKeys,
	cloudId,
	aggAbsoluteUrl,
}: {
	aggAbsoluteUrl?: string | undefined;
	cloudId: string;
	issueKeys: string[];
}): Promise<AggResponse<JiraHydrationData>> => {
	const variables = {
		issueKeys,
		cloudId,
	};

	const response = fetchJiraFieldsQuery({
		variables,
		aggAbsoluteUrl,
	});

	return response;
};
